import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPlus } from 'react-icons/fi';
import VaultHubNav from '../VaultHubNav/VaultHubNav';
import ScrollToTop from '../ScrollToTop/ScrollToTop';
import FooterSimple from '../Footer/FooterSimple';
import BrowseSidebarSticky from '../BrowseSidebarSticky/BrowseSidebarSticky';
import { BrowseOverlayContext } from '../../hooks/useBrowseOverlay';
import './MsvBrowseShell.css';
import '../../styles/msv-grid-shared.css';

const HUB_PRIMARY = {
    vault: { label: 'Add items', to: '/additems' },
    collectibles: { label: 'Add items', to: '/additems' },
    heroCards: { label: 'Add items', to: '/additems' },
};

const MsvBrowseShell = ({
    className = '',
    eyebrow,
    title,
    hubVariant = 'vault',
    sectionTitle,
    sectionEyebrow,
    resultCount,
    toolbar,
    sidebarBelowNav = null,
    belowToolbar = null,
    children,
    footer,
    overlay = null,
    showPrimaryAction = true,
    showSiteFooter = true,
}) => {
    const navigate = useNavigate();
    const stickyHeadRef = useRef(null);
    const primary = HUB_PRIMARY[hubVariant] || HUB_PRIMARY.vault;
    const [registeredOverlay, setRegisteredOverlay] = useState(null);
    const registerOverlay = useMemo(() => setRegisteredOverlay, []);

    useEffect(() => {
        const el = stickyHeadRef.current;
        if (!el) return undefined;

        const root = el.closest('.msv-browse-main');
        if (!root) return undefined;

        const syncTopbarHeight = () => {
            root.style.setProperty('--msv-browse-topbar-sticky-height', `${el.offsetHeight}px`);
        };

        syncTopbarHeight();
        const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(syncTopbarHeight) : null;
        observer?.observe(el);
        window.addEventListener('resize', syncTopbarHeight);

        return () => {
            observer?.disconnect();
            window.removeEventListener('resize', syncTopbarHeight);
        };
    }, [toolbar]);

    return (
        <BrowseOverlayContext.Provider value={registerOverlay}>
        <div className={`msv-browse-page${className ? ` ${className}` : ''}`}>
            <ScrollToTop />
            <aside className="msv-browse-sidebar">
                {/* Sticky inner: outer aside stretches with content so the footer
                    can push the whole rail up instead of covering it. */}
                <BrowseSidebarSticky>
                    {eyebrow && <p className="msv-page-eyebrow">{eyebrow}</p>}
                    {title && <h1>{title}</h1>}
                    {showPrimaryAction ? (
                        <button
                            type="button"
                            className="msv-browse-cta"
                            onClick={() => navigate(primary.to)}
                        >
                            <FiPlus aria-hidden />
                            {primary.label}
                        </button>
                    ) : (
                        <div className="msv-browse-cta-spacer" aria-hidden="true" />
                    )}
                    <p className="msv-browse-sidebar-label">Browse</p>
                    <VaultHubNav variant={hubVariant} layout="sidebar" />
                    {sidebarBelowNav}
                </BrowseSidebarSticky>
            </aside>

            <div className="msv-browse-main">
                <div className="msv-browse-main-body">
                    {toolbar && (
                        <div className="msv-browse-sticky-head" ref={stickyHeadRef}>
                            <div className="msv-browse-toolbar">{toolbar}</div>
                            {belowToolbar && (
                                <div className="msv-browse-below-toolbar">{belowToolbar}</div>
                            )}
                        </div>
                    )}

                    {(sectionTitle || resultCount != null) && (
                        <div className="msv-browse-heading-row">
                            <div className="msv-browse-heading-block">
                                {sectionEyebrow && <p className="msv-page-eyebrow">{sectionEyebrow}</p>}
                                {sectionTitle && <h2>{sectionTitle}</h2>}
                            </div>
                            {resultCount != null && (
                                <span>
                                    {resultCount} {resultCount === 1 ? 'result' : 'results'}
                                </span>
                            )}
                        </div>
                    )}

                    {children}
                </div>

                {footer && <div className="msv-browse-main-footer">{footer}</div>}
            </div>

            {showSiteFooter && (
                <div className="msv-browse-site-footer">
                    <FooterSimple />
                </div>
            )}

            {/* Outside sticky head so position:fixed pins to the viewport, not the sticky box */}
            <div className="msv-browse-chips-row" aria-label="Section navigation">
                <VaultHubNav variant={hubVariant} layout="chips" />
            </div>

            {/* Outside sticky toolbar so fixed drawers aren't trapped under the app header */}
            {overlay}
            {registeredOverlay}
        </div>
        </BrowseOverlayContext.Provider>
    );
};

export default MsvBrowseShell;
